import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { computeCard, fmtDate } from "@/lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ImageUpload } from "@/components/site/ImageUpload";
import { StatusBadge } from "@/components/site/StatusBadge";
import { Switch } from "@/components/ui/switch";

const EMPTY = { nom: "", postnom: "", prenom: "", sexe: "", date_naissance: "", telephone: "", province: "", ville: "", lieu_exercice: "", photo: null, cnap: "", date_delivrance: "", masque: false };

const F = ({ label, children, className = "" }) => (
  <div className={`space-y-1.5 ${className}`}><Label className="text-xs text-slate-600">{label}</Label>{children}</div>
);

export const APFormDialog = ({ open, onOpenChange, ap, onSaved }) => {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF(ap ? { ...EMPTY, ...ap, date_naissance: ap.date_naissance || "", date_delivrance: ap.date_delivrance || "" } : EMPTY); }, [open, ap]);

  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });
  const card = computeCard(f.date_delivrance);

  const submit = async (e) => {
    e.preventDefault();
    if (!/^\d{8}$/.test(f.cnap)) return toast.error("Le numéro CNAP doit contenir exactement 8 chiffres.");
    if (!f.sexe) return toast.error("Veuillez sélectionner le sexe.");
    setBusy(true);
    const payload = { ...f, date_naissance: f.date_naissance || null, date_delivrance: f.date_delivrance || null };
    ["id", "date_expiration", "statut", "jours_restants", "created_at", "updated_at"].forEach((k) => delete payload[k]);
    try {
      if (ap) await api.put(`/admin/aps/${ap.id}`, payload);
      else await api.post("/admin/aps", payload);
      toast.success(ap ? "AP mis à jour" : "AP enregistré");
      onSaved();
      onOpenChange(false);
    } catch (ex) {
      toast.error(errMsg(ex));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="ap-form-dialog" aria-describedby={undefined} className="max-w-3xl max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display">{ap ? "Modifier l'AP" : "Nouvel assistant en pharmacie"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-6">
          <div className="flex flex-col sm:flex-row gap-6">
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-600">Photo</Label>
              <ImageUpload value={f.photo} onChange={set("photo")} testId="ap-photo-upload" label="Ajouter la photo" tall />
            </div>
            <div className="flex-1 grid sm:grid-cols-3 gap-4">
              <F label="Nom *"><Input required data-testid="ap-nom-input" value={f.nom} onChange={set("nom")} /></F>
              <F label="Post-nom"><Input data-testid="ap-postnom-input" value={f.postnom} onChange={set("postnom")} /></F>
              <F label="Prénom"><Input data-testid="ap-prenom-input" value={f.prenom} onChange={set("prenom")} /></F>
              <F label="Sexe *">
                <Select value={f.sexe} onValueChange={set("sexe")}>
                  <SelectTrigger data-testid="ap-sexe-select"><SelectValue placeholder="Choisir" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="M" data-testid="ap-sexe-m">Masculin</SelectItem>
                    <SelectItem value="F" data-testid="ap-sexe-f">Féminin</SelectItem>
                  </SelectContent>
                </Select>
              </F>
              <F label="Date de naissance"><Input type="date" data-testid="ap-naissance-input" value={f.date_naissance} onChange={set("date_naissance")} /></F>
              <F label="Téléphone"><Input data-testid="ap-telephone-input" value={f.telephone} onChange={set("telephone")} /></F>
            </div>
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            <F label="Province"><Input data-testid="ap-province-input" value={f.province} onChange={set("province")} /></F>
            <F label="Ville"><Input data-testid="ap-ville-input" value={f.ville} onChange={set("ville")} /></F>
            <F label="Lieu d'exercice"><Input data-testid="ap-lieu-input" value={f.lieu_exercice} onChange={set("lieu_exercice")} /></F>
          </div>
          <div className="rounded-xl border border-emerald-900/10 bg-emerald-50/50 p-4 grid sm:grid-cols-3 gap-4 items-end">
            <F label="N° CNAP (8 chiffres) *">
              <Input required inputMode="numeric" maxLength={8} data-testid="ap-cnap-input" className="font-mono tracking-[0.2em]" value={f.cnap} onChange={(e) => setF({ ...f, cnap: e.target.value.replace(/\D/g, "").slice(0, 8) })} />
            </F>
            <F label="Date de délivrance carte verte"><Input type="date" data-testid="ap-delivrance-input" value={f.date_delivrance} onChange={set("date_delivrance")} /></F>
            <div className="space-y-1.5 text-sm">
              <div className="text-xs text-slate-600">Expiration (auto, +3 ans)</div>
              <div className="flex items-center gap-2 flex-wrap h-9">
                <span data-testid="ap-expiration-preview" className="font-semibold">{fmtDate(card.date_expiration)}</span>
                <StatusBadge statut={card.statut} testId="ap-status-preview" />
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3 rounded-xl border border-rose-200 bg-rose-50/60 p-4">
            <Switch id="masque" checked={!!f.masque} onCheckedChange={(v) => setF({ ...f, masque: v })} data-testid="ap-masque-switch" />
            <label htmlFor="masque" className="text-sm"><span className="font-semibold text-rose-700">Masquer cet AP</span> — pas en ordre de cotisation (le public verra « PAS EN ORDRE DE COTISATION »)</label>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="ap-form-cancel">Annuler</Button>
            <Button type="submit" disabled={busy} data-testid="ap-form-submit">{busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Enregistrer</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
